import { notification } from "antd";
import classNames from "classnames";
import { get } from "lodash";
import { useState } from "react";
import { BiCheck } from "react-icons/bi";
import {
  MdOutlineBookmark,
  MdOutlineCheckCircle,
  MdOutlineMap,
  MdOutlineWarning,
} from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { COLORS } from "shared/style/colors";
import { ADD_REFERENTIAL } from "utils/api/graphql/mutations/geo-zones";
import { REFERENTIALS } from "utils/api/graphql/queries/geo-zones";
import { REFERENTIAL_QUERY_LIMIT } from "../constants";
import StepCard from "../StepCard";

const ReferentialLoader = ({ referentialType, children }) => {
  const { data } = useQuery(REFERENTIALS, {
    variables: {
      where: { type: referentialType },
      take: REFERENTIAL_QUERY_LIMIT,
    },
    skip: !referentialType,
  });
  const refs = get(data, "referentials.data", []);
  return children(refs);
};

const SectionBlock = ({
  section,
  valueMap,
  onMapChange,
  shouldSave,
  onSaveChange,
  refName,
  onRefNameChange,
  selectedRefId,
  onSelectedRefIdChange,
  labels,
}) => {
  const { key, title, fileValues, options, referentialType } = section;
  if (!fileValues || fileValues.length === 0) return null;

  const mapped = fileValues.filter((v) => !!valueMap[v]).length;

  const renderStatus = () => {
    const isDone = mapped === fileValues.length;
    return (
      <div
        className={classNames("pricers-import__mapping-status", {
          "pricers-import__mapping-status--done": isDone,
          "pricers-import__mapping-status--pending": !isDone,
        })}
      >
        {isDone ? (
          <MdOutlineCheckCircle size={16} />
        ) : (
          <MdOutlineWarning size={16} />
        )}
        {isDone
          ? labels.ALL_MAPPED
          : `${fileValues.length - mapped} ${labels.REMAINING}`}
      </div>
    );
  };

  const handleLoadRef = (refId, refs) => {
    if (!refId) {
      onSelectedRefIdChange(null);
      return;
    }
    const ref = refs.find((r) => r.id === refId);
    if (!ref || !ref.entries) return;
    onMapChange((prev) => ({ ...prev, ...ref.entries }));
    onRefNameChange(ref.name);
    onSelectedRefIdChange(ref.id);
    onSaveChange(true);
  };

  return (
    <div className="pricers-import__vm-section">
      <div className="pricers-import__vm-section-header">
        <h4 className="m-0">{title}</h4>
        {renderStatus()}
      </div>

      {referentialType && (
        <ReferentialLoader referentialType={referentialType}>
          {(existingRefs) =>
            existingRefs.length > 0 ? (
              <div className="pricers-import__ref-loader">
                <MdOutlineMap size={20} color={COLORS.C_PRIMARY} />
                <span className="pricers-import__ref-loader-label">
                  {labels.LOAD_EXISTING}
                </span>
                <Select
                  options={existingRefs.map((r) => ({
                    label: r.name,
                    value: r.id,
                  }))}
                  placeholder={labels.SELECT_REFERENTIAL}
                  onChange={(refId) => handleLoadRef(refId, existingRefs)}
                  allowClear
                  className="w-250"
                />
              </div>
            ) : null
          }
        </ReferentialLoader>
      )}

      <div className="pricers-import__vm-rows">
        {fileValues.map((fileVal) => {
          const isMapped = !!valueMap[fileVal];
          return (
            <div
              key={fileVal}
              className={classNames("pricers-import__vm-row", {
                "pricers-import__vm-row--mapped": isMapped,
              })}
            >
              <div className="pricers-import__vm-file-value">
                <span
                  className={classNames("pricers-import__status-dot", {
                    "pricers-import__status-dot--mapped": isMapped,
                    "pricers-import__status-dot--required": !isMapped,
                  })}
                />
                <span className="pricers-import__file-value">
                  {fileVal}
                </span>
              </div>
              <div className="pricers-import__vm-arrow">&rarr;</div>
              <Select
                showSearch
                options={options}
                value={valueMap[fileVal] || undefined}
                placeholder={labels.SELECT_PLACEHOLDER}
                onChange={(val) =>
                  onMapChange((prev) => ({
                    ...prev,
                    [fileVal]: val,
                  }))
                }
                allowClear
                className="pricers-import__vm-select"
              />
            </div>
          );
        })}
      </div>

      {referentialType && (
        <div className="pricers-import__save-section">
          <div className="d-flex align--center gap-10">
            <input
              type="checkbox"
              checked={shouldSave}
              onChange={(e) => onSaveChange(e.target.checked)}
              className="pricers-checkbox"
            />
            <MdOutlineBookmark size={16} color={COLORS.C_PRIMARY} />
            <span className="pricers-import__save-label">
              {labels.SAVE_AS_REFERENTIAL}
            </span>
          </div>
          {shouldSave && (
            <Input
              placeholder={labels.REFERENTIAL_NAME_PLACEHOLDER}
              value={refName}
              onChange={(e) => onRefNameChange(e.target.value)}
              className="mt-10"
            />
          )}
        </div>
      )}
    </div>
  );
};

const StepValueMapping = ({
  sections,
  onConfirm,
  persistedState,
  onStateChange,
  onBack,
  backLabel,
  labels,
}) => {
  const [saving, setSaving] = useState(false);

  const activeSections = sections.filter(
    (s) => s.fileValues && s.fileValues.length > 0,
  );

  const [addReferential] = useMutation(ADD_REFERENTIAL);

  const getMap = (sectionKey) =>
    get(persistedState, `maps.${sectionKey}`, {});

  const setMap = (sectionKey, val) => {
    onStateChange((prev) => ({
      ...prev,
      maps: {
        ...prev.maps,
        [sectionKey]:
          typeof val === "function"
            ? val(get(prev, `maps.${sectionKey}`, {}))
            : val,
      },
    }));
  };

  const getSave = (sectionKey) =>
    get(persistedState, `save.${sectionKey}`, false);

  const setSave = (sectionKey, val) => {
    onStateChange((prev) => ({
      ...prev,
      save: { ...prev.save, [sectionKey]: val },
    }));
  };

  const getRefName = (sectionKey) =>
    get(persistedState, `refNames.${sectionKey}`, "");

  const setRefName = (sectionKey, val) => {
    onStateChange((prev) => ({
      ...prev,
      refNames: { ...prev.refNames, [sectionKey]: val },
    }));
  };

  const getSelectedRefId = (sectionKey) =>
    get(persistedState, `selectedRefIds.${sectionKey}`, null);

  const setSelectedRefId = (sectionKey, val) => {
    onStateChange((prev) => ({
      ...prev,
      selectedRefIds: {
        ...prev.selectedRefIds,
        [sectionKey]: val,
      },
    }));
  };

  const allMapped = activeSections.every((section) => {
    const valueMap = getMap(section.key);
    return section.fileValues.every((v) => !!valueMap[v]);
  });

  const canSave = activeSections.every((section) => {
    const shouldSave = getSave(section.key);
    const refName = getRefName(section.key);
    return !shouldSave || refName.trim();
  });

  const handleConfirm = async () => {
    setSaving(true);
    try {
      for (const section of activeSections) {
        const shouldSave = getSave(section.key);
        const refName = getRefName(section.key);
        if (!shouldSave || !refName.trim() || !section.referentialType) {
          continue;
        }
        const refData = {
          name: refName.trim(),
          type: section.referentialType,
          entries: getMap(section.key),
        };
        const selectedId = getSelectedRefId(section.key);
        if (selectedId) {
          refData.id = selectedId;
        }
        await addReferential({
          refetchQueries: [
            {
              query: REFERENTIALS,
              variables: {
                where: { type: section.referentialType },
                take: REFERENTIAL_QUERY_LIMIT,
              },
            },
          ],
          variables: { data: refData },
        });
      }
    } catch (error) {
      notification.error({
        message: labels.REFERENTIAL_SAVE_ERROR,
        description: error.message,
      });
    } finally {
      setSaving(false);
    }

    const mappings = {};
    for (const section of activeSections) {
      mappings[section.key] = getMap(section.key);
    }
    onConfirm(mappings);
  };

  const confirmButton = (
    <Button
      type="primary"
      onClick={handleConfirm}
      disabled={!allMapped || !canSave || saving}
      loading={saving}
    >
      <BiCheck size={20} />
      {labels.CONFIRM}
    </Button>
  );

  return (
    <StepCard
      title={labels.TITLE}
      description={labels.DESCRIPTION}
      onBack={onBack}
      backLabel={backLabel}
      actions={confirmButton}
    >
      {activeSections.map((section) => (
        <SectionBlock
          key={section.key}
          section={section}
          valueMap={getMap(section.key)}
          onMapChange={(val) => setMap(section.key, val)}
          shouldSave={getSave(section.key)}
          onSaveChange={(val) => setSave(section.key, val)}
          refName={getRefName(section.key)}
          onRefNameChange={(val) => setRefName(section.key, val)}
          selectedRefId={getSelectedRefId(section.key)}
          onSelectedRefIdChange={(val) =>
            setSelectedRefId(section.key, val)
          }
          labels={labels}
        />
      ))}
    </StepCard>
  );
};

export default StepValueMapping;
